import { useEffect, useState, type FormEvent } from 'react';
import type { MeDto } from '@bb/shared';
import { api, ApiError, emailFlows, family, social, type AuthStatus } from '../net/api';
import { ForgotPassword } from './EmailPages';
import { Field } from './Field';
import { TwoStepCode } from './TwoStep';
import { BannedAppeal } from './BannedAppeal';
import { LandingInfo, LandingNav } from './Landing';

/** Age in whole years from a YYYY-MM-DD date (null until a full date is entered). */
function ageFrom(d: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < day)) age--;
  return age;
}

export function Welcome({ notice, onSignedIn, onClosed }: { notice?: string; onSignedIn: (me: MeDto) => void; onClosed: (m: string) => void }) {
  // Invite emails link to /?invite=CODE: start on "Create account" with the code filled in.
  const [invite] = useState(() => new URLSearchParams(window.location.search).get('invite')?.slice(0, 20) ?? '');
  const [mode, setMode] = useState<'login' | 'register'>(invite ? 'register' : 'login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(notice);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [banned, setBanned] = useState<string | null>(null);
  const [appealing, setAppealing] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [ticket, setTicket] = useState<string>();
  const [unverified, setUnverified] = useState<{ login: string; password: string } | null>(null);
  const [resent, setResent] = useState<string>();
  const [birthDate, setBirthDate] = useState('');
  const [waitingParent, setWaitingParent] = useState<{ username: string; login?: string; password?: string } | null>(null);
  const age = ageFrom(birthDate);
  const [status, setStatus] = useState<AuthStatus>({ registrationOpen: true, inviteOnly: false });
  const ageClosed = age !== null ? signupClosedFor(status, age) : null;
  useEffect(() => {
    social.authStatus().then(setStatus, () => {});
  }, []);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(undefined);
    setFields({});
    try {
      const str = (k: string) => (f.get(k) ? String(f.get(k)).trim() : undefined);
      if (mode === 'login') {
        const r = await api.login({ login: String(f.get('login')), password: String(f.get('password')) });
        if ('twoStep' in r) setTicket(r.ticket);
        else onSignedIn(r);
        return;
      }
      const r = await api.register({
        username: String(f.get('username')),
        birthDate,
        email: str('email'),
        parentEmail: str('parentEmail'),
        password: String(f.get('password')),
        ...(status.inviteOnly || invite ? { inviteCode: String(f.get('inviteCode') ?? '') } : {}),
      });
      if ('underReview' in r) {
        setMode('login');
        setResent(`Thanks for signing up, ${r.username}! Before the account can be used, our team needs to review it. Reviews can take up to 14 business days, and we'll email you when it's done. Questions? support@buddybay.net`);
      } else if ('pendingParent' in r) setWaitingParent({ username: r.username, login: r.username, password: String(f.get('password')) });
      else if ('verifyEmail' in r) {
        // Same screen as signing in before confirming: they can resend from there.
        setUnverified({ login: r.username, password: String(f.get('password')) });
        setMode('login');
        setResent(`Almost done! We sent a link to ${r.email}. Click it to confirm your email, then sign in.`);
      } else onSignedIn(r);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'login_closed') return onClosed(err.message);
      if (err instanceof ApiError && err.code === 'account_banned') setBanned(String(f.get('login') ?? ''));
      if (err instanceof ApiError && err.code === 'parent_required') {
        setWaitingParent({ username: String(f.get('login')), login: String(f.get('login')), password: String(f.get('password')) });
        return;
      }
      if (err instanceof ApiError && err.code === 'email_unverified') setUnverified({ login: String(f.get('login')), password: String(f.get('password')) });
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="landing-page">
    <div className="welcome">
      <LandingNav />
      {status.banner && (
        <p className={`login-banner ${status.banner.tone}`} role="status">{status.banner.message}</p>
      )}
      <section className="welcome-hero">
        <h1 className="brand big">Buddy Bay</h1>
        <p className="tagline">A seaside town for little creatures and humans alike. Make a buddy, explore the bay, play games and meet friends.</p>
        <p className="hero-facts">Free to play · Ages 8+ · Plays in your browser</p>
        <a className="btn ghost hero-more" href="#about">Learn more about Buddy Bay ↓</a>
      </section>
      {waitingParent ? (
        <WaitingForParent {...waitingParent} onBack={() => { setWaitingParent(null); setMode('login'); }} />
      ) : appealing ? (
        <BannedAppeal login={banned ?? ''} onBack={() => setAppealing(false)} />
      ) : forgot ? (
        <ForgotPassword onBack={() => setForgot(false)} />
      ) : (
      <section className="panel narrow" id="sign-in">
        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'tab on' : 'tab'} onClick={() => setMode('login')}>Sign in</button>
          <button role="tab" aria-selected={mode === 'register'} className={mode === 'register' ? 'tab on' : 'tab'} onClick={() => setMode('register')}>Create account</button>
        </div>
        {ticket ? (
          <TwoStepCode ticket={ticket} onDone={onSignedIn} onBack={() => setTicket(undefined)} />
        ) : (
        <form onSubmit={submit} noValidate>
          {mode === 'login' ? (
            <>
              <Field id="login" name="login" label="Username or email" autoComplete="username" required error={fields.login} />
              <Field id="password" name="password" label="Password" type="password" autoComplete="current-password" required error={fields.password} />
            </>
          ) : (
            <>
              <Field
                id="birthDate"
                name="birthDate"
                label="Date of birth"
                type="date"
                required
                value={birthDate}
                onChange={(e) => setBirthDate(e.currentTarget.value)}
                max={new Date().toISOString().slice(0, 10)}
                error={fields.birthDate}
              />
              <Field id="username" name="username" label="Username" autoComplete="username" required maxLength={16} error={fields.username} />
              {age !== null && age > 14 && <Field id="email" name="email" label="Your email" type="email" autoComplete="email" required error={fields.email} />}
              {age !== null && age <= 16 && (
                <>
                  <Field id="parentEmail" name="parentEmail" label="Parent or guardian's email" type="email" required error={fields.parentEmail} />
                  <p className="muted small-print">Your parent or guardian needs their own Buddy Bay account first. Enter the email they signed up with, and we'll ask them to approve your account. You can play as soon as they do.</p>
                </>
              )}
              <Field id="password" name="password" label="Password (10+ characters)" type="password" autoComplete="new-password" required minLength={10} error={fields.password} />
              {(status.inviteOnly || invite) && <Field id="inviteCode" name="inviteCode" label="Invite code" autoComplete="off" defaultValue={invite} required={status.inviteOnly} error={fields.inviteCode} />}
              {!status.registrationOpen && <p className="muted small-print">New accounts are paused right now.</p>}
              {status.registrationOpen && ageClosed && <p className="form-error" role="status">{ageClosed}</p>}
            </>
          )}
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="btn wide" disabled={busy}>{busy ? 'One moment…' : mode === 'login' ? 'Sign in' : 'Create account'}</button>
          {banned !== null && <button type="button" className="btn ghost wide" onClick={() => setAppealing(true)}>Appeal this ban</button>}
          {unverified && (
            <button
              type="button"
              className="btn ghost wide"
              onClick={() => void emailFlows.resendSignedOut(unverified.login, unverified.password).then(() => setResent('Sent! Check your inbox (and spam).'), (e: Error) => setResent(e.message))}
            >
              Resend confirmation email
            </button>
          )}
          {resent && <p className="note-ok">{resent}</p>}
          {mode === 'login' && <button type="button" className="link-btn" onClick={() => setForgot(true)}>Forgot your password?</button>}
          {mode === 'register' && (
            <p className="muted small-print">By creating an account you agree to the <a href="/policies/terms">Terms of Service</a> and <a href="/policies/rules">Community Rules</a>, and you've read the <a href="/policies/privacy">Privacy Policy</a>.</p>
          )}
        </form>
        )}
      </section>
      )}
    </div>
    <LandingInfo />
    </div>
  );
}

const BRACKETS = [
  { key: 'child', label: '8–13', from: 0, to: 13 },
  { key: 'teen', label: '14–16', from: 14, to: 16 },
  { key: 'adult', label: '17+', from: 17, to: 999 },
] as const;

/** The same message the server gives when sign-up is closed to this age (the server still decides). */
function signupClosedFor(status: AuthStatus, age: number): string | null {
  const rules = status.signupAges;
  if (!rules || age < 8) return null;
  if (age < rules.minAge) return `Sorry! New accounts are only open to players ${rules.minAge} and older right now. Please check back later.`;
  const b = BRACKETS.find((x) => age >= x.from && age <= x.to)!;
  if (rules.closedBrackets.includes(b.key)) return `Sorry! New accounts for players aged ${b.label} aren't open right now. Please check back later.`;
  return null;
}

/** Shown after a 16-and-under signs up, or tries to sign in before a parent approved. */
function WaitingForParent({ username, login, password, onBack }: { username: string; login?: string; password?: string; onBack: () => void }) {
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [changing, setChanging] = useState(false);
  async function resend(parentEmail?: string) {
    if (!login || !password) return;
    try {
      const r = await family.resendConsent(login, password, parentEmail);
      setMsg({ kind: 'ok', text: r.alreadyApproved ? 'Good news: your parent already approved! Sign in to play.' : 'Sent! Ask your parent to check their inbox (and spam).' });
      setChanging(false);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  }
  return (
    <section className="panel narrow">
      <h2>Almost there, {username}!</h2>
      <p>We emailed your parent or guardian. Once they approve your account, come back and sign in to play.</p>
      <p className="muted small-print">If nobody approves it within 14 days, the account is deleted and you can sign up again.</p>
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      {login && password && (
        changing ? (
          <form onSubmit={(e) => { e.preventDefault(); void resend(String(new FormData(e.currentTarget).get('parentEmail'))); }}>
            <Field id="newParentEmail" name="parentEmail" label="Parent or guardian's email" type="email" required />
            <button className="btn wide">Send to this email</button>
          </form>
        ) : (
          <div className="row-wrap">
            <button className="btn ghost" onClick={() => void resend()}>Send the email again</button>
            <button className="btn ghost" onClick={() => setChanging(true)}>Use a different email</button>
          </div>
        )
      )}
      <button className="btn wide" onClick={onBack}>Back to sign in</button>
    </section>
  );
}
